import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import {
  EDimensionUnit,
  EWeightUnit,
  type IAdminProductDetail,
  type IAdminWarehouse,
  type IBrand,
  type ICurrencyPrice,
  type IPhoto,
  type IProductWarehouse,
  type ITranslation,
  type ITranslationArray,
  type TPropertiesProductSave,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import {
  type IDescriptionHistoryEntry,
  type IProductDashboard,
  type IProductState,
  ProductPhotoPositionChange,
} from './types';

export const preloadedState: IProductState = {
  brand: { uuid: '', name: '' },
  mpn: '',
  names: [],
  uuid: '',
  inProcess: false,
  descriptions: [],
  slugs: [],
  metaDescriptions: [],
  metaKeywords: [],
  categories: { uuids: [] },
  photos: [],
  nextDeliveryDate: '',
  prices: [],
  dimensions: {
    weight: 0,
    width: 0,
    height: 0,
    length: 0,
    weightUnit: EWeightUnit.KG,
    dimensionUnit: EDimensionUnit.M,
  },
  productWarehouses: [],
  gtin: '',
  relatedUUIDs: [],
  relatedProductsFromElasticSearch: undefined,
  descriptionHistory: [],
  descriptionHistoryInProcess: false,
  dashboard: undefined,
  dashboardInProcess: false,
  isActive: true,
  updatedAt: undefined,
  featureFlags: [],
  taxTypes: [],
  endPriceMarginRate: 0,
};

const setProductFromDetail = (
  state: IProductState,
  product: IAdminProductDetail,
): void => {
  state.uuid = product.uuid;
  state.brand = product.brand;
  state.mpn = product.mpn;
  state.gtin = product.gtin;
  state.categories = product.categories;
  state.descriptions = product.descriptions;
  state.dimensions = product.dimensions;
  state.names = product.names;
  state.nextDeliveryDate = product.nextDeliveryDate;
  state.photos = product.photos.sort((a: IPhoto, b: IPhoto): number =>
    numberCompare(a.position, b.position),
  );
  state.prices = product.prices;
  state.relatedUUIDs = product.relatedUUIDs;
  state.slugs = product.slugs;
  state.productWarehouses = product.productWarehouses;
  state.isActive = product.isActive;
  state.featureFlags = product.featureFlags;
  state.taxTypes = product.taxTypes;
  state.endPriceMarginRate = product.endPriceMarginRate;
  state.metaDescriptions = product.metaDescriptions ?? [];
  state.metaKeywords = product.metaKeywords ?? [];
};

const photoChangePosition = (
  photoUUID: string,
  direction: ProductPhotoPositionChange,
  photos: IPhoto[],
): IPhoto[] => {
  const photo = photos.find(({ uuid }) => uuid === photoUUID);
  if (!photo) return photos;

  const newPosition = photo.position + direction;
  const prevPhoto = photos.find(({ position }) => position === newPosition);
  if (prevPhoto) prevPhoto.position -= direction;
  photo.position = newPosition;
  photos.sort((a, b) => a.position - b.position);
  return photos;
};

const productSlice = createSlice({
  name: 'Product',
  initialState: preloadedState,
  reducers: {
    load: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(uuid: string) {
        return { payload: { product: { uuid } } };
      },
    },
    loadResponse: {
      reducer(
        state,
        action: { payload: { product: IAdminProductDetail | null } },
      ) {
        if (action.payload.product) {
          setProductFromDetail(state, action.payload.product);
        }
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(product: IAdminProductDetail | null) {
        return { payload: { product } };
      },
    },
    saveCategories: {
      reducer(
        state,
        action: {
          payload: {
            product: { uuid: string };
            categories: { uuids: string[] };
          };
        },
      ) {
        state.inProcess = true;
        state.categories = { uuids: action.payload.categories.uuids };
      },
      prepare(uuid: string, uuids: string[]) {
        return { payload: { product: { uuid }, categories: { uuids } } };
      },
    },
    saveCategoriesResponse: {
      reducer(state) {
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    photoDelete: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, photoUUID: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            photo: { uuid: photoUUID },
          },
        };
      },
    },
    photoDeleteResponse: {
      reducer(state, action: { payload: { photo: { uuid: string } } }) {
        state.photos = state.photos.filter(
          (p: IPhoto) => p.uuid !== action.payload.photo.uuid,
        );
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(photoUUID: string) {
        return { payload: { photo: { uuid: photoUUID } } };
      },
    },
    photoPositionChange: {
      reducer(
        state,
        action: {
          payload: {
            photo: { uuid: string };
            direction: ProductPhotoPositionChange;
          };
        },
      ) {
        state.photos = photoChangePosition(
          action.payload.photo.uuid,
          action.payload.direction,
          state.photos,
        );
        state.updatedAt = Date.now();
      },
      prepare(photoUUID: string, direction: ProductPhotoPositionChange) {
        return { payload: { photo: { uuid: photoUUID }, direction } };
      },
    },
    photoPositionSave: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, photos: IPhoto[]) {
        return { payload: { product: { uuid: productUUID }, photos } };
      },
    },
    photoPositionSaveResponse: {
      reducer(state) {
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    photoVisibleChange: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, photoUUID: string, isVisible: boolean) {
        return {
          payload: {
            product: { uuid: productUUID },
            photo: { uuid: photoUUID },
            isVisible,
          },
        };
      },
    },
    photoVisibleChangeResponse: {
      reducer(
        state,
        action: { payload: { photo: { uuid: string }; isVisible: boolean } },
      ) {
        const photo = state.photos.find(
          (p: IPhoto) => p.uuid === action.payload.photo.uuid,
        );
        if (photo) {
          photo.isVisible = action.payload.isVisible;
        }
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(photoUUID: string, isVisible: boolean) {
        return { payload: { photo: { uuid: photoUUID }, isVisible } };
      },
    },
    nameLive: {
      reducer(
        state,
        action: { payload: { name: string; languageType: { code: string } } },
      ) {
        const name = state.names.find(
          ({ code }: ITranslation) => code === action.payload.languageType.code,
        );
        if (name) {
          name.value = action.payload.name;
        } else {
          state.names.push({
            code: action.payload.languageType.code,
            value: action.payload.name,
          });
        }
      },
      prepare(name: string, languageCode: string) {
        return { payload: { name, languageType: { code: languageCode } } };
      },
    },
    descriptionLive: {
      reducer(
        state,
        action: {
          payload: { description: string; languageType: { code: string } };
        },
      ) {
        const desc = state.descriptions.find(
          ({ code }: ITranslation) => code === action.payload.languageType.code,
        );
        if (desc) {
          desc.value = action.payload.description;
        } else {
          state.descriptions.push({
            code: action.payload.languageType.code,
            value: action.payload.description,
          });
        }
      },
      prepare(description: string, languageCode: string) {
        return {
          payload: { description, languageType: { code: languageCode } },
        };
      },
    },
    slugLive: {
      reducer(
        state,
        action: { payload: { slug: string; languageType: { code: string } } },
      ) {
        const slug = state.slugs.find(
          ({ code }: ITranslation) => code === action.payload.languageType.code,
        );
        if (slug) {
          slug.value = action.payload.slug;
        } else {
          state.slugs.push({
            code: action.payload.languageType.code,
            value: action.payload.slug,
          });
        }
      },
      prepare(slug: string, languageCode: string) {
        return { payload: { slug, languageType: { code: languageCode } } };
      },
    },
    metaDescriptionLive: {
      reducer(
        state,
        action: {
          payload: { metaDescription: string; languageType: { code: string } };
        },
      ) {
        const metaDesc = state.metaDescriptions.find(
          ({ code }: ITranslation) => code === action.payload.languageType.code,
        );
        if (metaDesc) {
          metaDesc.value = action.payload.metaDescription;
        } else {
          state.metaDescriptions.push({
            code: action.payload.languageType.code,
            value: action.payload.metaDescription,
          });
        }
      },
      prepare(metaDescription: string, languageCode: string) {
        return {
          payload: { metaDescription, languageType: { code: languageCode } },
        };
      },
    },
    metaKeywordsLive: {
      reducer(
        state,
        action: {
          payload: { metaKeywords: string[]; languageType: { code: string } };
        },
      ) {
        const metaKw = state.metaKeywords.find(
          ({ code }) => code === action.payload.languageType.code,
        );
        if (metaKw) {
          metaKw.value = action.payload.metaKeywords;
        } else {
          state.metaKeywords.push({
            code: action.payload.languageType.code,
            value: action.payload.metaKeywords,
          });
        }
      },
      prepare(metaKeywords: string[], languageCode: string) {
        return {
          payload: { metaKeywords, languageType: { code: languageCode } },
        };
      },
    },
    namesDescriptionsSlugsUpdate: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        uuid: string,
        names: ITranslation[],
        descriptions: ITranslation[],
        slugs: ITranslation[],
        metaDescriptions: ITranslation[],
        metaKeywords: ITranslationArray[],
      ) {
        return {
          payload: {
            product: { uuid },
            names,
            descriptions,
            slugs,
            metaDescriptions,
            metaKeywords,
          },
        };
      },
    },
    namesDescriptionsSlugsUpdateResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    copyDescriptions: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productSourceUUID: string, productDestinationUUID: string) {
        return {
          payload: {
            product: {
              uuid: productDestinationUUID,
              sourceUUID: productSourceUUID,
            },
          },
        };
      },
    },
    copyDescriptionsResponse: {
      reducer(
        state,
        action: { payload: { product: IAdminProductDetail | null } },
      ) {
        if (action.payload.product) {
          setProductFromDetail(state, action.payload.product);
        }
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(product: IAdminProductDetail | null) {
        return { payload: { product } };
      },
    },
    dimensionsUpdate: {
      reducer(
        state,
        action: {
          payload: {
            dimensions: {
              weight: number;
              width: number;
              height: number;
              length: number;
            };
          };
        },
      ) {
        state.inProcess = true;
        state.dimensions.weight = action.payload.dimensions.weight;
        state.dimensions.width = action.payload.dimensions.width;
        state.dimensions.height = action.payload.dimensions.height;
        state.dimensions.length = action.payload.dimensions.length;
      },
      prepare(
        uuid: string,
        weight: number,
        width: number,
        height: number,
        length: number,
      ) {
        return {
          payload: {
            product: { uuid },
            dimensions: {
              weight,
              width,
              height,
              length,
              dimensionUnit: EDimensionUnit.M,
              weightUnit: EWeightUnit.KG,
            },
          },
        };
      },
    },
    dimensionsUpdateResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    stockUpdate: {
      reducer(state, action: { payload: { warehouses: IAdminWarehouse[] } }) {
        state.inProcess = true;
        action.payload.warehouses.forEach(
          (warehouse: IAdminWarehouse): void => {
            const productWarehouse: IProductWarehouse | undefined =
              state.productWarehouses.find(
                ({ uuid }: IProductWarehouse): boolean =>
                  uuid === warehouse.uuid,
              );
            if (productWarehouse) {
              productWarehouse.preOrderDays = warehouse.preOrderDays;
              productWarehouse.count = warehouse.count;
              productWarehouse.url = warehouse.url;
              productWarehouse.isSyncActive = warehouse.isSyncActive;
              productWarehouse.syncCount = warehouse.syncCount;
              productWarehouse.syncDimensions = warehouse.syncDimensions;
              productWarehouse.endPriceMarginRate =
                warehouse.endPriceMarginRate;
            }
          },
        );
      },
      prepare(
        productUUID: string,
        nextDeliveryDate: Date | undefined,
        warehouses: IAdminWarehouse[],
      ) {
        return {
          payload: {
            product: { uuid: productUUID },
            nextDeliveryDate,
            warehouses,
          },
        };
      },
    },
    stockUpdateResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    currenciesPricesUpdate: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(uuid: string, currencyPrices: ICurrencyPrice[]) {
        return { payload: { product: { uuid }, currencyPrices } };
      },
    },
    currenciesPricesUpdateResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    brandSet: {
      reducer(
        state,
        action: { payload: { brand: IBrand; mpn: string; gtin: string } },
      ) {
        state.brand = action.payload.brand;
        state.mpn = action.payload.mpn;
        state.gtin = action.payload.gtin;
        state.inProcess = true;
      },
      prepare(uuid: string, brand: IBrand, mpn: string, gtin: string) {
        return { payload: { product: { uuid }, brand, mpn, gtin } };
      },
    },
    brandSetResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    productRecalculatePrices: {
      reducer(
        state,
        action: {
          payload: {
            currencyType: { isoCode: string };
            priceWithoutTax: number;
          };
        },
      ) {
        const prices = state.prices.filter(
          ({ isoCode }: ICurrencyPrice): boolean =>
            isoCode !== action.payload.currencyType.isoCode,
        );
        prices.push({
          isoCode: action.payload.currencyType.isoCode,
          pricesWithTax: [],
          priceWithoutTax: action.payload.priceWithoutTax,
        });
        state.prices = prices;
        state.updatedAt = Date.now();
      },
      prepare(productUUID: string, isoCode: string, priceWithoutTax: number) {
        return {
          payload: {
            product: { uuid: productUUID },
            currencyType: { isoCode },
            priceWithoutTax,
          },
        };
      },
    },
    productPropertiesSave: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, productProperties: TPropertiesProductSave) {
        return {
          payload: { product: { uuid: productUUID }, productProperties },
        };
      },
    },
    productPropertiesSaveResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    productAssignRelatedProducts: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, relatedUUIDs: string[]) {
        return { payload: { product: { uuid: productUUID, relatedUUIDs } } };
      },
    },
    productAssignRelatedProductsResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    photoDownloadFromUrlsAction: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, urls: string[]) {
        return { payload: { product: { uuid: productUUID }, urls } };
      },
    },
    photoDownloadFromUrlsResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    productsFindInElasticSearch: {
      reducer(state) {
        state.relatedProductsFromElasticSearch = undefined;
        state.inProcess = true;
      },
      prepare(languageCode: string, query: string) {
        return { payload: { languageType: { code: languageCode }, query } };
      },
    },
    productsFindInElasticSearchResponse: {
      reducer(state, action: { payload: { productUUIDs: string[] } }) {
        state.relatedProductsFromElasticSearch = action.payload.productUUIDs;
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(productUUIDs: string[]) {
        return { payload: { productUUIDs } };
      },
    },
    productSetActive: {
      reducer(state, action: { payload: { isActive: boolean } }) {
        state.isActive = action.payload.isActive;
        state.inProcess = true;
      },
      prepare(productUUID: string, isActive: boolean) {
        return { payload: { product: { uuid: productUUID }, isActive } };
      },
    },
    productFeatureFlagDestroy: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, featureFlagCode: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            featureFlag: { code: featureFlagCode },
          },
        };
      },
    },
    productFeatureFlagDestroyResponse: {
      reducer(state, action: { payload: { featureFlag: { code: string } } }) {
        state.featureFlags = state.featureFlags.filter(
          (featureFlag): boolean =>
            featureFlag.code !== action.payload.featureFlag.code,
        );
        state.inProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(featureFlagCode: string) {
        return { payload: { featureFlag: { code: featureFlagCode } } };
      },
    },
    productAIEnhanceDescription: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, languageTypeCode: string) {
        return {
          payload: { product: { uuid: productUUID }, languageTypeCode },
        };
      },
    },
    productAITranslateNameAllLanguages: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, languageTypeCode: string) {
        return {
          payload: { product: { uuid: productUUID }, languageTypeCode },
        };
      },
    },
    productAIEnhanceDescriptionAllLanguages: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, languageTypeCode: string) {
        return {
          payload: { product: { uuid: productUUID }, languageTypeCode },
        };
      },
    },
    productAIGenerateMeta: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, languageTypeCode: string) {
        return {
          payload: { product: { uuid: productUUID }, languageTypeCode },
        };
      },
    },
    productAIGenerateMetaAllLanguages: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, languageTypeCode: string) {
        return {
          payload: { product: { uuid: productUUID }, languageTypeCode },
        };
      },
    },
    productSetEndPriceMarginRate: {
      reducer(state, action: { payload: { endPriceMarginRate: number } }) {
        state.endPriceMarginRate = action.payload.endPriceMarginRate;
        state.inProcess = true;
      },
      prepare(productUUID: string, endPriceMarginRate: number) {
        return {
          payload: { product: { uuid: productUUID }, endPriceMarginRate },
        };
      },
    },
    productSetEndPriceMarginRateResponse: {
      reducer(state) {
        state.updatedAt = Date.now();
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    descriptionHistoryLoad: {
      reducer(state) {
        state.descriptionHistory = [];
        state.descriptionHistoryInProcess = true;
      },
      prepare(uuid: string) {
        return { payload: { product: { uuid } } };
      },
    },
    descriptionHistoryLoadResponse: {
      reducer(
        state,
        action: { payload: { history: IDescriptionHistoryEntry[] } },
      ) {
        state.descriptionHistory = action.payload.history;
        state.descriptionHistoryInProcess = false;
      },
      prepare(history: IDescriptionHistoryEntry[]) {
        return { payload: { history } };
      },
    },
    productDashboardLoad: {
      reducer(state) {
        state.dashboardInProcess = true;
      },
      prepare(uuid: string, daysInterval: number) {
        return { payload: { product: { uuid }, daysInterval } };
      },
    },
    productDashboardLoadResponse: {
      reducer(state, action: { payload: { dashboard: IProductDashboard } }) {
        state.dashboard = action.payload.dashboard;
        state.dashboardInProcess = false;
      },
      prepare(dashboard: IProductDashboard) {
        return { payload: { dashboard } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Product'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  load,
  loadResponse,
  saveCategories,
  saveCategoriesResponse,
  photoDelete,
  photoDeleteResponse,
  photoPositionChange,
  photoPositionSave,
  photoPositionSaveResponse,
  photoVisibleChange,
  photoVisibleChangeResponse,
  nameLive,
  descriptionLive,
  slugLive,
  metaDescriptionLive,
  metaKeywordsLive,
  namesDescriptionsSlugsUpdate,
  namesDescriptionsSlugsUpdateResponse,
  copyDescriptions,
  copyDescriptionsResponse,
  dimensionsUpdate,
  dimensionsUpdateResponse,
  stockUpdate,
  stockUpdateResponse,
  currenciesPricesUpdate,
  currenciesPricesUpdateResponse,
  brandSet,
  brandSetResponse,
  productRecalculatePrices,
  productPropertiesSave,
  productPropertiesSaveResponse,
  productAssignRelatedProducts,
  productAssignRelatedProductsResponse,
  photoDownloadFromUrlsAction,
  photoDownloadFromUrlsResponse,
  productsFindInElasticSearch,
  productsFindInElasticSearchResponse,
  productSetActive,
  productFeatureFlagDestroy,
  productFeatureFlagDestroyResponse,
  productAIEnhanceDescription,
  productAITranslateNameAllLanguages,
  productAIEnhanceDescriptionAllLanguages,
  productAIGenerateMeta,
  productAIGenerateMetaAllLanguages,
  productSetEndPriceMarginRate,
  productSetEndPriceMarginRateResponse,
  descriptionHistoryLoad,
  descriptionHistoryLoadResponse,
  productDashboardLoad,
  productDashboardLoadResponse,
} = productSlice.actions;
export default productSlice.reducer;
